/**
 * State H's grouping. Ten error kinds reach five headings (Drift §U75: the mock drew the
 * five it could show), and `GROUP_OF` is a total record so an eleventh kind is a compile
 * error here and not a row that renders nowhere.
 */
import { IMPORT_ERROR_KINDS, type ImportErrorKind } from "@packagepage/shared";

export const ERROR_GROUPS = [
  "check_digit",
  "prefix",
  "excel",
  "values",
  "duplicate",
] as const;
export type ErrorGroup = (typeof ERROR_GROUPS)[number];

export const GROUP_OF: Readonly<Record<ImportErrorKind, ErrorGroup>> = {
  check_digit: "check_digit",
  outside_prefix: "prefix",
  excel_mangled: "excel",
  duplicate_gtin: "duplicate",
  unreadable_date: "values",
  invalid_lot: "values",
  invalid_language: "values",
  unknown_attribute: "values",
  attribute_above_tier: "values",
  invalid_value: "values",
};

export const KIND_LABEL: Readonly<Record<ImportErrorKind, string>> = {
  check_digit: "Check digit doesn't match",
  outside_prefix: "GTIN isn't in a prefix you have verified",
  excel_mangled: "GTIN turned into scientific notation",
  duplicate_gtin: "GTIN appears more than once in the file",
  unreadable_date: "Date we can't read",
  invalid_lot: "Batch lot that GS1 doesn't allow",
  invalid_language: "Language code we don't recognise",
  unknown_attribute: "Attribute we don't hold",
  attribute_above_tier: "Attribute your plan doesn't include",
  invalid_value: "Value that doesn't fit its field",
};

export interface GroupCopy {
  readonly title: string;
  /** The rule, the likely cause and the fix: three sentences, never the row's own `detail`. */
  readonly rule: string;
  readonly cause: string;
  readonly fix: string;
}

export const GROUP_COPY: Readonly<Record<ErrorGroup, GroupCopy>> = {
  check_digit: {
    title: "Check digit doesn't match",
    rule: "The last digit of a GTIN is calculated from the other thirteen.",
    cause:
      "Usually a typing slip, or a digit lost when a spreadsheet re-saved the number.",
    fix: "Check each number against your source. The right-hand column shows what the last digit should be. It is information only: we never write a different GTIN into your catalogue.",
  },
  prefix: {
    title: "GTIN isn't in your prefix",
    rule: "We publish pages only for GTINs that start with a company prefix you have verified.",
    cause:
      "The GTIN belongs to another brand's prefix, or your own prefix is not verified yet.",
    fix: "If you make these products for another brand, they belong in that brand's account. If the prefix is yours, verify it on your dashboard and upload the file again.",
  },
  excel: {
    title: "Excel turned the GTIN into science",
    rule: "A GTIN has to arrive with every digit.",
    cause:
      "Excel rewrites a long number as scientific notation, such as 8.71E+12. The original digits are gone from the file.",
    fix: "Export the file again with the barcode column formatted as Text, then upload it again.",
  },
  values: {
    title: "A value we can't read",
    rule: "Each cell has to fit the field its column is matched to.",
    cause:
      "A date written in an order we can't tell, a batch lot with characters GS1 doesn't allow, a language code or attribute we don't hold, or a required value left empty.",
    fix: "Correct the cells named below in your spreadsheet. Write dates as YYYY-MM-DD, which can only be read one way.",
  },
  duplicate: {
    title: "Same GTIN twice in the file",
    rule: "Each GTIN may appear on one row of a file.",
    cause: "The same product is listed on two rows, sometimes under different names.",
    fix: "Keep one row for each GTIN. We don't pick one for you.",
  },
};

export interface GroupSummary {
  readonly group: ErrorGroup;
  readonly total: number;
  readonly kinds: readonly { readonly kind: ImportErrorKind; readonly count: number }[];
}

/**
 * The groups that have problems, in `ERROR_GROUPS` order, each kind once with its count from
 * `stats.byKind`. A word in `byKind` that is not a kind is dropped: no CHECK holds the list.
 */
export function summariseGroups(
  byKind: Readonly<Record<string, number>> | undefined,
): GroupSummary[] {
  const counts = byKind ?? {};
  return ERROR_GROUPS.flatMap((group) => {
    const kinds = IMPORT_ERROR_KINDS.filter(
      (kind) => GROUP_OF[kind] === group && (counts[kind] ?? 0) > 0,
    ).map((kind) => ({ kind, count: counts[kind] ?? 0 }));
    if (kinds.length === 0) return [];
    return [{ group, total: kinds.reduce((n, k) => n + k.count, 0), kinds }];
  });
}

/** The company prefix a GTIN-14 starts with once the indicator digit is dropped: seven digits, as the validator names it. */
export function filePrefixOf(cell: string): string | null {
  const digits = cell.trim();
  if (!/^\d{1,14}$/.test(digits)) return null;
  return digits.padStart(14, "0").slice(1, 8);
}
